import type {
  CaseRole,
  RemedialAction,
  RemedialActionHumanEdit,
  RemedialActionHumanEditPatch,
} from "@/services/api/case-checking";
import { ruleIdsFor } from "@/components/case-checking/remedialCoverage";

export type ActionEditTag = "new" | "edited" | "reassigned";

export type EffectiveRemedialAction = RemedialAction & {
  tags: ActionEditTag[];
  agentAction?: RemedialAction;
  /** Soft-removed in guided review (still shown until undo or refresh). */
  removed?: boolean;
  /** Server auto-removed because linked rules now pass (distinct from human delete). */
  autoRemoved?: boolean;
};

function tagsForEdit(agent: RemedialAction | undefined, edit: RemedialActionHumanEdit): ActionEditTag[] {
  const tags: ActionEditTag[] = [];
  if (edit.created) tags.push("new");
  if (!agent) return tags.length ? tags : ["new"];

  if (edit.role && edit.role !== agent.role) tags.push("reassigned");
  if (
    edit.title !== agent.title ||
    edit.description !== agent.description ||
    (edit.priority && edit.priority !== agent.priority)
  ) {
    tags.push("edited");
  }
  return [...new Set(tags)];
}

function editToAction(edit: RemedialActionHumanEdit): RemedialAction {
  return {
    id: edit.action_id,
    title: edit.title,
    description: edit.description,
    role: (edit.role ?? "planner") as CaseRole,
    priority: (edit.priority as RemedialAction["priority"]) || "medium",
    source: "review",
    rule_ids: edit.rule_ids ?? [],
    rule_id: edit.rule_ids?.[0] ?? null,
    status: "pending",
  };
}

export function mergeRemedialActions(
  agentActions: RemedialAction[],
  edits: RemedialActionHumanEdit[],
): EffectiveRemedialAction[] {
  const agentById = new Map(agentActions.map((a) => [a.id, a]));
  const editById = new Map(edits.map((e) => [e.action_id, e]));
  const out: EffectiveRemedialAction[] = [];

  for (const agent of agentActions) {
    const edit = editById.get(agent.id);
    if (edit?.deleted) {
      const autoRemoved = edit.auto_removed_reason === "rules_resolved";
      out.push({
        ...agent,
        tags: tagsForEdit(agent, edit),
        agentAction: agent,
        removed: true,
        autoRemoved,
      });
      continue;
    }
    if (!edit) {
      out.push({ ...agent, tags: [] });
      continue;
    }
    const merged = { ...agent, ...editToAction(edit), id: agent.id };
    out.push({
      ...merged,
      tags: tagsForEdit(agent, edit),
      agentAction: agent,
    });
  }

  for (const edit of edits) {
    if (!edit.created || agentById.has(edit.action_id)) continue;
    const action = editToAction(edit);
    out.push({
      ...action,
      tags: tagsForEdit(undefined, edit),
      removed: Boolean(edit.deleted),
      autoRemoved: edit.deleted && edit.auto_removed_reason === "rules_resolved",
    });
  }

  return out;
}

export function isOpenEffectiveAction(action: EffectiveRemedialAction): boolean {
  if (action.removed) return false;
  return action.status === "pending" || action.status === "in_progress";
}

export function effectiveRuleIds(action: EffectiveRemedialAction): string[] {
  return ruleIdsFor(action);
}

/** Build a PATCH payload that round-trips server fields. */
export function humanEditToPatch(
  edit: RemedialActionHumanEdit,
  overrides: Partial<RemedialActionHumanEditPatch> = {},
): RemedialActionHumanEditPatch {
  return {
    action_id: edit.action_id,
    deleted: edit.deleted,
    created: edit.created,
    title: edit.title,
    description: edit.description,
    role: edit.role,
    priority: edit.priority,
    rule_ids: edit.rule_ids ?? [],
    edited_at: edit.edited_at,
    auto_removed_reason: edit.auto_removed_reason,
    ...overrides,
  };
}
